/**
 * Map absolute explorer paths to folder name segments relative to a tree API root.
 * Used for Documents drill-down / breadcrumbs (see getDocumentsDrillDownView).
 * Must match server `normalizeRoot` in explorer-db.js.
 */
export function normalizeExplorerPath(p: string): string {
  return String(p || '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '')
}

export function folderPathToNameSegmentsUnderRoot(treeRoot: string, folderPath: string): string[] {
  const root = normalizeExplorerPath(treeRoot)
  const fp = normalizeExplorerPath(folderPath)
  if (!root || !fp.startsWith(root)) return []
  const rel = fp.slice(root.length).replace(/^\//, '')
  if (!rel) return []
  return rel.split('/').filter(Boolean)
}

/** Parent folder path for a file or folder path (strip final segment). */
export function parentFolderPath(path: string): string {
  const t = path.replace(/\/$/, '')
  const i = t.lastIndexOf('/')
  return i <= 0 ? '' : t.slice(0, i)
}

/** Absolute folder path from tree API root + name segments (inverse of folderPathToNameSegmentsUnderRoot). */
export function folderPathFromNameSegmentsUnderRoot(treeRoot: string, segments: string[]): string {
  const root = normalizeExplorerPath(treeRoot)
  if (!segments.length) return root
  return [root, ...segments].join('/')
}

/** True when `path` is the folder or a descendant file/folder under `folderPath`. */
export function isPathUnderFolder(path: string, folderPath: string): boolean {
  const p = normalizeExplorerPath(path)
  const folder = normalizeExplorerPath(folderPath)
  if (!folder) return true
  return p === folder || p.startsWith(`${folder}/`)
}

/**
 * Whether opening `filePath` should keep Browse drill-down focus and scroll (routine folder browsing).
 * Explicit reveal-in-sidebar passes scrollToNode and always resets.
 */
export function shouldPreserveBrowseStateOnDocumentOpen(
  filePath: string,
  opts: {
    scrollToNode: boolean
    selectedProjectId?: string | null
    focusSegments?: string[]
    treeRoot?: string
  }
): boolean {
  if (opts.scrollToNode) return false
  const normFile = normalizeExplorerPath(filePath)
  if (!normFile) return false

  if (opts.selectedProjectId && isPathUnderFolder(normFile, opts.selectedProjectId)) {
    return true
  }

  const segments = opts.focusSegments ?? []
  if (segments.length > 0 && opts.treeRoot) {
    const focusedFolder = folderPathFromNameSegmentsUnderRoot(opts.treeRoot, segments)
    if (isPathUnderFolder(normFile, focusedFolder)) return true
  }

  return false
}
